import React, { 
  useState, 
  useEffect, 
  createContext, 
  useContext, 
  useReducer, 
} from "react";
import api from 'state/axios';
import { Snackbar, Alert } from "@mui/material";


const defaultColumns = [
  { id: "todo", title: "To Do", color: "bg-blue-500" },
  { id: "in-progress", title: "In Progress", color: "bg-yellow-500" },
  { id: "done", title: "Done", color: "bg-green-500" },
];

const initialState = {
  tasks: [],
  columns: defaultColumns,
};

const KanbanContext = createContext();

function kanbanReducer(state, action) {
  switch (action.type) {
    case "ADD_TASK":
      return { ...state, tasks: [...state.tasks, action.task] };
    case "UPDATE_TASK":
      return {
        ...state,
        tasks: state.tasks.map((task) => (task._id === action.task._id ? action.task : task)),
      };
    case "DELETE_TASK":
      return {
        ...state,
        tasks: state.tasks.filter((task) => task._id !== action.taskId),
      };
    case "MOVE_TASK":
      return {
        ...state,
        tasks: state.tasks.map((task) =>
          task._id === action.taskId ? { ...task, status: action.destinationColumn } : task
        ),
      };
    case "ADD_COLUMN":
      return { ...state, columns: [...state.columns, action.column] };
    case "UPDATE_COLUMN":
      return {
        ...state,
        columns: state.columns.map((column) =>
          column.id === action.column.id ? action.column : column
        ),
      };
    case "DELETE_COLUMN":
      return {
        ...state,
        columns: state.columns.filter((column) => column.id !== action.columnId),
        tasks: state.tasks.filter((task) => task.status !== action.columnId),
      };
    case "LOAD_DATA":
      return action.data;
    default:
      return state;
  }
}


export function KanbanProvider({ children }) {
  const [state, dispatch] = useReducer(kanbanReducer, initialState);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* ===== MODAL STATE ===== */
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingColumn, setEditingColumn] = useState(null);

  const openCreateTask = () => {
    setEditingTask(null);
    setIsTaskModalOpen(true);
  };

  const openCreateColumn = () => {
    setEditingColumn(null);
    setIsColumnModalOpen(true);
  };


  /* ===== SNACKBAR ===== */
  const showNotification = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };


  /* ===== LOAD INITIAL DATA ===== */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(
          '/tasks/load'
        );
        dispatch({ type: "LOAD_DATA", data: res.data });
      } catch (error) {
        showNotification(error.response?.data.message || "Failed to load Kanban data!", "error");
      }
    };

    fetchData();
  }, []);


  /* ===== API FUNCTIONS ===== */
  const addTask = async (taskData) => {
    try {
      const res = await api.post(
        '/tasks/new', 
        taskData
      );

      dispatch({ type: "ADD_TASK", task: res.data });
      showNotification("Task added successfully", "success");
    } catch (error) {
      showNotification(error.response?.data.message || "Failed to add task", "error");
    }
  };

  const updateTask = async (task) => {
    try {
      const res = await api.put(
        `/tasks/update/${task._id}`, 
        task
      );

      dispatch({ type: "UPDATE_TASK", task: res.data });
      showNotification("Task updated successfully", "success");
    } catch (error) {
      showNotification(error.response?.data.message || "Failed to update task", "error");
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await api.delete(
        `/tasks/delete/${taskId}`
      );

      dispatch({ type: "DELETE_TASK", taskId });
      showNotification("Task deleted successfully", "success");
    } catch (error) {
      showNotification(error.response?.data.message || "Failed to delete task", "error");
    }
  };

  const moveTask = async (taskId, sourceColumn, destinationColumn) => {
    try {
      const destinationColumnExists = state.columns.some((col) => 
        col.id === destinationColumn
      );

      if (!destinationColumnExists) {
        showNotification(`Column "${destinationColumn}" does not exist`, "error");
        return;
      }

      await api.patch(
        `/tasks/move/${taskId}`,
        { sourceColumn, destinationColumn }
      );

      dispatch({ type: "MOVE_TASK", taskId, destinationColumn });
      const columnTitle =
        state.columns.find((c) => c.id === destinationColumn)?.title ||
        destinationColumn;

      showNotification(`Task moved to ${columnTitle}`, "success");
    } catch (error) {
      showNotification(
        error.response?.data.message || "Failed to move task",
        "error"
      );
    }
  };

  const addColumn = async (columnData) => {
    try {
      const id = columnData.title
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
      if (state.columns.some((col) => col.id === id)) {
        showNotification("A column with a similar name already exists", "error");
        return;
      }
      const newColumn = { id, ...columnData };

      const res = await api.post(
        '/tasks/column/new', 
        newColumn
      );
      dispatch({ type: "ADD_COLUMN", column: res.data });
      showNotification("Column added successfully", "success");
    } catch (error) {
      showNotification(
        error.response?.data.message || "Failed to add column",
        "error"
      );
    }
  };

  const updateColumn = async (column) => {
    try {
      const res = await api.put(
        `/tasks/column/update/${column.id}`,
        column
      );
      dispatch({ type: "UPDATE_COLUMN", column: res.data });
      showNotification("Column updated successfully", "success");
    } catch (error) {
      showNotification(
        error.response?.data.message || "Failed to update column",
        "error"
      );
    }
  };

  const deleteColumn = async (columnId) => {
    try {
      await api.delete(
        `/tasks/column/delete/${columnId}`
      );
      dispatch({ type: "DELETE_COLUMN", columnId });
      showNotification("Column deleted successfully", "success");
    } catch (error) {
      showNotification(
        error.response?.data.message || "Failed to delete column",
        "error"
      );
    }
  };

  const saveData = async () => {
    try {
      await api.post(
        '/tasks/save',
        { tasks: state.tasks, columns: state.columns }
      );
      showNotification("Project data saved successfully", "success");
    } catch (error) {
      showNotification(
        error.response?.data.message || "Failed to save data",
        "error"
      );
    }
  };

  const loadData = async () => {
    try {
      const res = await api.get(
        '/tasks/load'
      );
      dispatch({ type: "LOAD_DATA", data: res.data });
      showNotification("Project data loaded successfully", "success");
    } catch (error) {
      showNotification(
        error.response?.data.message || "No saved data found",
        "error"
      );
    }
  };


  return (
    <KanbanContext.Provider
      value={{
        state,
        addTask,
        updateTask,
        deleteTask,
        moveTask,
        addColumn,
        updateColumn,
        deleteColumn,
        saveData,
        loadData,

        isTaskModalOpen,
        setIsTaskModalOpen,
        isColumnModalOpen,
        setIsColumnModalOpen,
        editingTask,
        setEditingTask,
        editingColumn,
        setEditingColumn,
        openCreateTask,
        openCreateColumn,
      }}
    >
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </KanbanContext.Provider>
  );
}

export function useKanban() {
  const context = useContext(KanbanContext);
  if (context === undefined) {
    throw new Error("useKanban must be used within a KanbanProvider");
  }
  return context;
}